import React, { useState, useEffect, useRef } from 'react'
import { T, Spinner, Empty, SectionLabel, RatingBadge, playColor, Avatar, GameLogos } from '../ui'
import { getNFLSlate, getNFLResults, getNFLProof } from '../api'
import { drawStudioCard, drawProofCard, shareCanvas } from '../share'
import { track } from '../track'
import { SlipCheckButton } from '../slipcheck'
import { playText, teamNick } from '../plain'

// Play Lab (formerly "Card Studio") — the generator. Pick WHAT (a stat) and WHICH GAMES, tap
// Generate, and get the model's highest-probability plays as a ranked, tappable list — then
// optionally turn it into a shareable card (canvas image, share.js drawStudioCard). Every list and
// card is framed as model output with the "research, not advice · 21+" footer. No stake sizing /
// bet language ever renders. The graded share cards (Weekly Recap, Results) sit below.

const CARD_TYPES = [
  ['recap',    'Weekly Recap', 'Record + calibration'],
  ['scorers',  'Top Scorers', 'Anytime TD, model-ranked'],
  ['market',   'By Market',   'Pick a stat'],
  ['gameplan', 'Game Plan',   'Featured plays, ranked'],
  ['results',  'Results',     'Graded ✓ / ✗ + record'],
]
// The markets a play can carry (matches getNFLResults' p.market labels).
const MARKETS = ['Receptions', 'Rec Yds', 'Rush Yds', 'Pass Yds', 'Pass TDs', 'Anytime TD']

const stateLabel = g => g?.state === 'final' ? 'FINAL' : g?.state === 'live' ? 'LIVE' : 'UPCOMING'
const stateColor = g => g?.state === 'final' ? T.muted2 : g?.state === 'live' ? T.blue : T.green
const _todayStr = () => new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()
// prob comes back 0-1; be tolerant of a 0-100 value too.
const pctOf = p => Math.round((((p || 0) > 1 ? (p || 0) / 100 : (p || 0))) * 100)
const isTD = m => m === 'Anytime TD'
const PROJ_FOOTER = 'Calibrated model probabilities · research, not advice · 21+'
const GRADED_FOOTER = 'Graded at settlement · research, not advice · 21+'

// Build the drawStudioCard args + share metadata for the SELECTED games' plays.
// gamesSel is an array of game objects; the card spans all of them (single game =
// a matchup card, several = a slate card ranked across the games).
function buildCard({ plays, type, gamesSel, market, week, totalGames }) {
  const single = gamesSel.length === 1 ? gamesSel[0] : null
  const all = totalGames > 0 && gamesSel.length === totalGames
  const scopeTitle = single ? `${single.away?.abbr || '?'} @ ${single.home?.abbr || '?'}`
    : all ? `Week ${week} · full slate` : `Week ${week} · ${gamesSel.length} games`
  const scopeFile = single ? `${single.away?.abbr}-${single.home?.abbr}`
    : all ? `week${week}-slate` : `week${week}-${gamesSel.length}g`
  const kicker = `${_todayStr()} · WEEK ${week ?? ''}`.trim()
  const ids = new Set(gamesSel.map(g => g.game_id))
  const mine = (plays || []).filter(p => ids.has(p.game_id))
  const byProb = (a, b) => (b.prob || 0) - (a.prob || 0)
  // Across multiple games, the opponent tag disambiguates who's who at a glance.
  const metaOf = p => `${p.pos ? p.pos + ' · ' : ''}${p.team || ''}${p.opp ? ` · vs ${teamNick(p.opp)}` : ''}`

  if (type === 'results') {
    const graded = mine.filter(p => p.status === 'hit' || p.status === 'miss').sort(byProb)
    const wins = graded.filter(p => p.status === 'hit').length
    const losses = graded.filter(p => p.status === 'miss').length
    const rows = graded.slice(0, 16).map(p => {
      const td = isTD(p.market)
      const value = td
        ? (p.status === 'hit' ? 'scored' : 'no TD')
        : (p.actual != null ? String(p.actual) : (p.status === 'hit' ? 'cleared' : 'under'))
      const bet = td ? 'Anytime TD' : `${playText(p.market, p.line)}`
      return { name: p.name, meta: metaOf(p), value, sub: bet, status: p.status, hot: p.status === 'hit' }
    })
    return {
      draw: { brandKicker: kicker, eyebrow: `Graded results · Week ${week}`, title: scopeTitle,
        subtitle: `Featured board · ${wins}-${losses}`, rows, footer: GRADED_FOOTER },
      filename: `linebrkr-nfl-${scopeFile}-results.png`,
      shareText: `LINEBRKR — ${scopeTitle} graded ${wins}-${losses} on the featured board. Research, not advice · 21+`,
      empty: rows.length ? null : 'No graded plays for the selected game(s) yet — results land as they finalize.',
    }
  }

  // Projection cards — probabilities, framed as model output.
  let pool = mine, eyebrow = '', titleSub = ''
  if (type === 'scorers') {
    pool = mine.filter(p => isTD(p.market)); eyebrow = `Top scorers · Week ${week}`
    titleSub = 'Most likely to find the end zone'
  } else if (type === 'market') {
    pool = mine.filter(p => p.market === market); eyebrow = `${market} · Week ${week}`
    titleSub = `Model-ranked · ${market}`
  } else if (type === 'gameplan') {
    pool = mine.filter(p => p.featured); eyebrow = `Game plan · Week ${week}`
    titleSub = "The model's featured plays, ranked"
  }
  const seen = new Set()
  const ranked = pool.slice().sort(byProb).filter(p => {
    const k = `${p.name}·${p.market}`; if (seen.has(k)) return false; seen.add(k); return true
  })
  const rows = ranked.slice(0, 16).map(p => {
    const td = isTD(p.market)
    // A "✓ value" flag ONLY where the model beats the price (edge_pct>0) — model output.
    const hasValue = p.edge_pct != null && p.edge_pct > 0
    const marketBit = type === 'scorers' ? 'Anytime TD' : p.market
    const meta = `${metaOf(p)} · ${marketBit}${hasValue ? ' · ✓ value' : ''}`
    const sub = td ? 'to score' : (p.line != null ? playText(p.market, p.line) : 'to hit')
    return { name: p.name, meta, value: `${pctOf(p.prob)}%`, sub, status: null, hot: hasValue }
  })
  return {
    draw: { brandKicker: kicker, eyebrow, title: scopeTitle, subtitle: titleSub, rows, footer: PROJ_FOOTER },
    filename: `linebrkr-nfl-${scopeFile}-${type}.png`,
    shareText: `LINEBRKR — ${scopeTitle} ${eyebrow.toLowerCase()}, model-ranked. Research, not advice · 21+`,
    empty: rows.length ? null : 'No model plays for this pick yet — check back once the board loads.',
  }
}

// Weekly Recap — the record card. Pulls the honest RECOMMENDED-tier record from /nfl/proof
// (STRONG/BEST BET/ELITE only), leads with the week's W-L, and backs it with the season
// record, season hit rate, top-20 rate and calibration. Rendered by the shared proof-card
// engine. Graded numbers only — no picks, no stake language.
function buildRecap(proof, week) {
  const ov = proof?.overall || {}
  const wk = (proof?.weeks || []).find(w => w.week === week)
  const t20 = proof?.top20 || {}
  const brier = proof?.calibration?.brier
  const hero = (wk && wk.plays) ? wk : ov          // this week's card, or the season if the week isn't settled yet
  if (!hero || !hero.plays) {
    return { empty: 'No graded recommended plays yet — the recap fills in once games settle.' }
  }
  const big = `${hero.wins ?? 0}-${hero.losses ?? 0}`
  const usingWeek = hero === wk
  const bigLabel = usingWeek ? `Week ${week} recommended record` : 'Season recommended record'
  const context = hero.hit_rate != null ? `${Math.round(hero.hit_rate)}% hit rate · ${hero.plays} plays` : `${hero.plays} plays`
  const tiles = []
  if (usingWeek && ov.plays) tiles.push({ label: 'Season record', value: `${ov.wins}-${ov.losses}`, good: true })
  if (ov.hit_rate != null) tiles.push({ label: 'Season hit rate', value: `${Math.round(ov.hit_rate)}%`, good: ov.hit_rate >= 55 })
  if (t20.hit_rate != null) tiles.push({ label: 'Top-20 plays', value: `${Math.round(t20.hit_rate)}%`, good: true })
  if (brier != null) tiles.push({ label: 'Calibration', value: Number(brier).toFixed(3), good: true })
  const scope = usingWeek ? `Week ${week}` : 'Season'
  return {
    draw: { eyebrow: usingWeek ? `WEEK ${week} · GRADED` : 'SEASON · GRADED', big, bigLabel, context, tiles },
    filename: `linebrkr-nfl-week${week}-recap.png`,
    shareText: `LINEBRKR — ${scope} recommended record ${big}. Calibrated model results · research, not advice · 21+`,
    empty: null,
  }
}

// What to generate — the stat chips at the top of Play Lab. `type`/`market` feed buildCard.
const GOALS = [
  { id: 'td',    label: 'Touchdowns',   icon: '🏈', type: 'scorers', market: 'Anytime TD' },
  { id: 'rec',   label: 'Receptions',   icon: '🙌', type: 'market',  market: 'Receptions' },
  { id: 'recyd', label: 'Rec Yds',      icon: '📡', type: 'market',  market: 'Rec Yds' },
  { id: 'rush',  label: 'Rush Yds',     icon: '🏃', type: 'market',  market: 'Rush Yds' },
  { id: 'pass',  label: 'Pass Yds',     icon: '🎯', type: 'market',  market: 'Pass Yds' },
  { id: 'ptd',   label: 'Pass TDs',     icon: '🔥', type: 'market',  market: 'Pass TDs' },
  { id: 'best',  label: 'Best overall', icon: '⭐', type: 'gameplan', market: null },
]
const SHARE_TYPES = [['recap', 'Weekly Recap', 'Record + calibration'], ['results', 'Results', 'Graded ✓ / ✗ for picked games']]
const MARKET_KEY = { 'Anytime TD': 'anytime_td', 'Receptions': 'receptions', 'Rec Yds': 'rec_yards',
  'Rush Yds': 'rush_yards', 'Pass Yds': 'pass_yards', 'Pass TDs': 'pass_tds' }
const PREF = 'brkr_nfl_playlab'
const loadPref = () => { try { return JSON.parse(localStorage.getItem(PREF)) || {} } catch (_) { return {} } }

const chip = on => ({ cursor: 'pointer', borderRadius: 10, fontWeight: 800,
  border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card, color: on ? T.green : T.muted })
const stepLabel = { fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.muted, margin: '14px 0 8px' }

// Same ranking rules as the share card: the chosen games' plays for the chosen stat, deduped,
// sorted by the shown probability (the backend's calibrated hit_prob).
function rankPlays(plays, goal, ids) {
  let pool = (plays || []).filter(p => ids.has(p.game_id) && p.status !== 'hit' && p.status !== 'miss' && p.status !== 'push')
  if (goal.type === 'scorers') pool = pool.filter(p => isTD(p.market))
  else if (goal.type === 'market') pool = pool.filter(p => p.market === goal.market)
  else pool = pool.filter(p => p.featured)
  const seen = new Set()
  return pool.slice().sort((a, b) => (b.prob || 0) - (a.prob || 0)).filter(p => {
    const k = `${p.name}·${p.market}`; if (seen.has(k)) return false; seen.add(k); return true })
}

function PlayRow({ p, i, onPlayer, onAddLeg, slipKeys }) {
  const prob = (p.prob || 0) > 1 ? p.prob / 100 : (p.prob || 0)
  const td = isTD(p.market)
  const prop = MARKET_KEY[p.market]
  const added = slipKeys && prop && slipKeys.has(`${p.gsis_id}:${prop}`)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: T.card, border: `1px solid ${T.border}`,
      borderRadius: 11, padding: '10px 11px', marginBottom: 7 }}>
      <span style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 800, color: T.faint, width: 18, textAlign: 'center' }}>{i + 1}</span>
      <Avatar url={p.headshot} name={p.name} team={p.team} size={34} />
      <button onClick={() => onPlayer && onPlayer({ full_name: p.name, gsis_id: p.gsis_id, pos: p.pos, team: p.team, opp: p.opp,
          market: p.market, prop_type: prop, status: 'active' })}
        style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: T.text }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {td ? 'Anytime TD' : playText(p.market, p.line)}</div>
        <div style={{ fontSize: 11, color: T.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {teamNick(p.team)} vs {teamNick(p.opp)}</div>
      </button>
      {p.tier && <RatingBadge tier={p.tier} size={22} />}
      <span style={{ textAlign: 'right', flexShrink: 0, minWidth: 46 }}>
        <span style={{ display: 'block', fontFamily: T.mono, fontSize: 17, fontWeight: 900, color: playColor(prob, p) }}>{Math.round(prob * 100)}%</span>
        <span style={{ display: 'block', fontSize: 9, color: T.faint, fontWeight: 700 }}>{td ? 'to score' : 'to hit'}</span>
      </span>
      {onAddLeg && prop && p.gsis_id && (
        <button aria-label={added ? 'Added to parlay' : 'Add to parlay'} disabled={added}
          onClick={() => { track('parlay_add', p.market); onAddLeg({ gsis_id: p.gsis_id, prop, market: p.market, name: p.name, team: p.team, opp: p.opp, pos: p.pos, line: p.line }) }}
          style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 8, cursor: added ? 'default' : 'pointer', fontWeight: 900, fontSize: 15,
            border: `1px solid ${T.green}${added ? '' : '66'}`, background: added ? T.green : 'transparent', color: added ? '#04140D' : T.green }}>
          {added ? '✓' : '+'}</button>
      )}
    </div>
  )
}

export default function PlayLab({ season, week, initialGameId, onPlayer, onAddLeg, slipKeys }) {
  const pref = useRef(loadPref()).current
  const [games, setGames] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sample, setSample] = useState(false)
  const [goalId, setGoalId] = useState(GOALS.some(g => g.id === pref.goal) ? pref.goal : 'td')
  const [scope, setScope] = useState(pref.scope === 'pick' ? 'pick' : 'all')   // all upcoming games | pick games
  const [selIds, setSelIds] = useState([])
  const [topN, setTopN] = useState([5, 10, 15].includes(pref.n) ? pref.n : 10)
  const [busy, setBusy] = useState(false)
  const [list, setList] = useState(null)          // generated ranked plays
  const [err, setErr] = useState('')
  const [preview, setPreview] = useState(null)    // share-card dataURL
  const [shareOpen, setShareOpen] = useState(false)
  const [shareType, setShareType] = useState('recap')
  const playsRef = useRef(null)                   // cached /results?scope=all for this week
  const cvRef = useRef(null)
  const shareRef = useRef({ file: 'linebrkr-nfl-card.png', text: '' })
  const resultsRef = useRef(null)

  useEffect(() => {
    let a = true; setLoading(true); playsRef.current = null
    getNFLSlate(season, week).then(res => {
      if (!a) return
      setGames(res.games || []); setSample(!!res._sample); setLoading(false)
    })
    return () => { a = false }
  }, [season, week])
  useEffect(() => { try { localStorage.setItem(PREF, JSON.stringify({ goal: goalId, scope, n: topN })) } catch (_) {} }, [goalId, scope, topN])

  // Arriving from a game's "Make a card" shortcut: pick just that game.
  useEffect(() => {
    if (!initialGameId || !games?.length) return
    if (games.some(x => x.game_id === initialGameId)) { setScope('pick'); setSelIds([initialGameId]); reset() }
  }, [initialGameId, games])

  const goal = GOALS.find(g => g.id === goalId) || GOALS[0]
  const upcoming = (games || []).filter(g => g.state !== 'final')
  const scopeGames = scope === 'all' ? upcoming : (games || []).filter(g => selIds.includes(g.game_id))
  function reset() { setList(null); setPreview(null); setErr('') }
  const toggleGame = g => { setSelIds(ids => ids.includes(g.game_id) ? ids.filter(x => x !== g.game_id) : [...ids, g.game_id]); reset() }

  const getPlays = async () => {
    if (!playsRef.current) { const d = await getNFLResults(season, week, 'all'); playsRef.current = (d && d.plays) || [] }
    return playsRef.current
  }

  const generate = async () => {
    if (busy || loading) return
    if (!scopeGames.length) { setErr(scope === 'all' ? 'Every game this week is final — pick games to see their graded results below.' : 'Pick at least one game.'); return }
    setBusy(true); setErr(''); setPreview(null)
    try {
      const ranked = rankPlays(await getPlays(), goal, new Set(scopeGames.map(g => g.game_id)))
      setList(ranked)
      track('playlab_generate', `${goal.id}:${scope === 'all' ? 'all' : scopeGames.length + 'g'}:${topN}`)
      if (!ranked.length) setErr('No model plays for this pick yet — lines and projections fill in through the week.')
      setTimeout(() => resultsRef.current && resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    } catch (_) { setErr('Could not generate right now — try again shortly.') }
    setBusy(false)
  }

  // Turn the generated list (or a graded share card) into an image.
  const makeCard = async (type) => {
    track('playlab_card', type === 'list' ? goal.id : type)
    if (busy) return
    setBusy(true); setErr('')
    try {
      let built, cv
      if (type === 'recap') {
        built = buildRecap(await getNFLProof(season), week)
        if (built.empty) { setErr(built.empty); setBusy(false); return }
        cv = drawProofCard(built.draw)
      } else {
        const sel = type === 'results' ? (games || []).filter(g => selIds.includes(g.game_id)) : scopeGames
        if (!sel.length) { setErr(type === 'results' ? 'Pick the games to grade under "Which games?" first.' : 'Pick at least one game.'); setBusy(false); return }
        built = buildCard({ plays: await getPlays(), type: type === 'results' ? 'results' : goal.type, gamesSel: sel,
          market: goal.market, week, totalGames: (games || []).length })
        if (type !== 'results') built.draw.rows = built.draw.rows.slice(0, topN)
        if (built.empty) { setErr(built.empty); setBusy(false); return }
        cv = drawStudioCard(built.draw)
      }
      cvRef.current = cv; shareRef.current = { file: built.filename, text: built.shareText }
      setPreview(cv.toDataURL('image/png'))
    } catch (_) { setErr('Could not build the card right now — try again shortly.') }
    setBusy(false)
  }
  const save = () => { if (cvRef.current) shareCanvas(cvRef.current, shareRef.current.file, shareRef.current.text) }
  const shown = list ? list.slice(0, topN) : null

  return (
    <div style={{ padding: '12px 16px 90px' }}>
      <div style={{ background: 'linear-gradient(120deg,#0c2018,#0a1119)', border: `1px solid ${T.green}44`, borderRadius: 14, padding: '13px 14px' }}>
        <div style={{ fontSize: 17, fontWeight: 900 }}>🧪 Generate a play list</div>
        <div style={{ fontSize: 12, color: T.muted2, marginTop: 3, lineHeight: 1.45 }}>
          Pick the games and a stat — get the model's highest-probability plays, ranked. Tap any player for the full breakdown.</div>
      </div>
      {sample && <div style={{ fontSize: 10, color: T.amber, marginTop: 8, letterSpacing: '0.05em' }}>● Live slate unavailable right now — check back shortly</div>}
      <SlipCheckButton season={season} week={week} onPlayer={onPlayer} compact />

      {/* Games first, then the stat (UX feedback: people think "which game" before "which prop"). */}
      <div style={stepLabel}>1 · Which games?</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 11, padding: 3 }}>
        {[['all', `All games${upcoming.length ? ` · ${upcoming.length}` : ''}`], ['pick', `Pick games${selIds.length ? ` · ${selIds.length}` : ''}`]].map(([k, lbl]) => {
          const on = scope === k
          return <button key={k} onClick={() => { setScope(k); reset() }} aria-pressed={on} style={{ cursor: 'pointer', border: 'none', borderRadius: 8,
            padding: '9px 0', fontSize: 12.5, fontWeight: 800, color: on ? '#04140D' : T.muted, background: on ? T.green : 'transparent' }}>{lbl}</button>
        })}
      </div>
      {scope === 'pick' && (loading ? <Spinner /> : !games?.length ? <Empty message="No games scheduled" /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginTop: 8 }}>
          {games.map(g => {
            const on = selIds.includes(g.game_id), fin = g.state === 'final'
            return <button key={g.game_id} onClick={() => toggleGame(g)} aria-pressed={on}
              style={{ ...chip(on), padding: '8px 2px 7px', fontSize: 12, opacity: fin && !on ? 0.55 : 1 }}>
              <GameLogos away={g.away?.abbr} home={g.home?.abbr} size={26} names color={on ? T.green : undefined} />
              <span style={{ display: 'block', fontSize: 9.5, fontWeight: 700, color: fin ? T.muted2 : T.faint, marginTop: 3 }}>
                {fin ? 'FINAL' : g.state === 'live' ? 'LIVE' : [g.day, g.time].filter(Boolean).join(' ')}</span>
            </button>
          })}
        </div>
      ))}

      <div style={stepLabel}>2 · What are you looking for?</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7 }}>
        {GOALS.map(g => {
          const on = g.id === goalId
          return <button key={g.id} onClick={() => { setGoalId(g.id); reset() }} aria-pressed={on}
            style={{ ...chip(on), padding: '10px 4px', fontSize: 12.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              gridColumn: g.id === 'best' ? 'span 3' : undefined }}>
            <span style={{ fontSize: 17 }}>{g.icon}</span>{g.label}{g.id === 'best' && <span style={{ fontSize: 10, fontWeight: 600, color: T.muted }}>the model's featured plays, every stat</span>}
          </button>
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: T.muted, marginRight: 4 }}>Show</span>
        {[5, 10, 15].map(n => <button key={n} onClick={() => setTopN(n)} aria-pressed={topN === n}
          style={{ ...chip(topN === n), padding: '6px 12px', fontSize: 12 }}>Top {n}</button>)}
      </div>

      <button onClick={generate} disabled={busy || loading} style={{ width: '100%', marginTop: 12, padding: '14px 0', fontSize: 15.5, fontWeight: 900,
        background: T.green, color: '#04140D', border: 'none', borderRadius: 11, cursor: busy || loading ? 'default' : 'pointer', opacity: busy || loading ? 0.6 : 1 }}>
        {loading ? 'Loading this week’s games…' : busy && !shareOpen ? 'Generating…' : list ? `Regenerate · ${goal.label}` : `Generate · ${goal.label}`}
      </button>
      {err && <div style={{ fontSize: 12, color: T.amber, marginTop: 10, lineHeight: 1.4 }}>{err}</div>}

      <div ref={resultsRef} style={{ scrollMarginTop: 120 }}>
        {shown && shown.length > 0 && (
          <>
            <SectionLabel>{goal.label} · top {shown.length}{scope === 'all' ? ' · all games' : ` · ${scopeGames.length} game${scopeGames.length === 1 ? '' : 's'}`}</SectionLabel>
            {shown.map((p, i) => <PlayRow key={`${p.gsis_id}:${p.market}`} p={p} i={i} onPlayer={onPlayer} onAddLeg={onAddLeg} slipKeys={slipKeys} />)}
            <div style={{ fontSize: 10.5, color: T.faint, margin: '2px 2px 10px', lineHeight: 1.5 }}>
              % = the model's calibrated chance at the offered line (our projected line where no book line is posted yet).
              {onAddLeg ? ' Tap + to add a leg to your parlay.' : ''} Research, not advice · 21+.</div>
            <button onClick={() => { setShareOpen(false); makeCard('list') }} disabled={busy} style={{ width: '100%', padding: '12px 0', fontSize: 14,
              fontWeight: 800, background: T.card, color: T.green, border: `1px solid ${T.green}`, borderRadius: 10, cursor: 'pointer' }}>
              📤 Make this a share card</button>
          </>
        )}
      </div>

      {preview && (
        <div style={{ marginTop: 12 }}>
          <img src={preview} alt="Share card preview" style={{ width: '100%', borderRadius: 12, border: `1px solid ${T.border}`, display: 'block' }} />
          <button onClick={save} style={{ width: '100%', marginTop: 10, padding: '12px 0', fontSize: 14, fontWeight: 800, background: T.green,
            color: '#04140D', border: 'none', borderRadius: 10, cursor: 'pointer' }}>Save / Share ⤴</button>
        </div>
      )}

      {/* Graded share cards — the week's record and graded results, for posting. */}
      <button onClick={() => setShareOpen(o => !o)} style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
        marginTop: 18, background: 'none', border: 'none', borderTop: `1px solid ${T.border}`, padding: '14px 2px 4px', cursor: 'pointer', color: T.muted2 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800 }}>📸 More share cards · Weekly Recap, graded Results</span>
        <span style={{ color: T.green, fontWeight: 900 }}>{shareOpen ? '–' : '+'}</span>
      </button>
      {shareOpen && (
        <div style={{ marginTop: 6 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            {SHARE_TYPES.map(([id, label, desc]) => {
              const on = id === shareType
              return <button key={id} onClick={() => { setShareType(id); setPreview(null); setErr('') }} style={{ ...chip(on), textAlign: 'left', padding: '11px 12px' }}>
                <div style={{ fontSize: 13.5, color: on ? T.green : T.text }}>{label}</div>
                <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, marginTop: 2 }}>{desc}</div>
              </button>
            })}
          </div>
          <button onClick={() => makeCard(shareType)} disabled={busy} style={{ width: '100%', padding: '12px 0', fontSize: 14, fontWeight: 800,
            background: T.card, color: T.green, border: `1px solid ${T.green}`, borderRadius: 10, cursor: 'pointer' }}>
            {busy ? 'Building…' : `Build ${shareType === 'recap' ? 'Weekly Recap' : 'Results'} card`}</button>
        </div>
      )}
    </div>
  )
}
